import groupBy from 'lodash.groupby';
import { inject } from 'mobx-react';
import React from 'react';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import styled from 'styled-components';

import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import { ADVANCED_SEARCH_URL } from '../../model/productType';
import ProductStore, { Product } from '../../store/productStore';

const Message = styled.p`
  margin: 8px 0;
  text-align: center;
`;

type Props = {
  hasResults: boolean;
  productStore: ProductStore;
  searchTerm: string;
} & InjectedIntlProps &
  AnalyticsConsumerProps;

const SEARCHABLE_PRODUCTS = Object.keys(ADVANCED_SEARCH_URL);

const ProductAdvancedSearchLinks = ({ analytics, intl, hasResults, searchTerm, productStore }: Props) => {
  const allowedProducts = productStore.availableProducts.filter(product => {
    return SEARCHABLE_PRODUCTS.includes(product.productName);
  });

  const groupedProducts = groupBy(allowedProducts, 'productName');
  const productKeys = Object.keys(groupedProducts);
  const body = intl.formatMessage(messages.clickOnAdvancedSearchLinks);

  return (
    <React.Fragment>
      <Message>{body}</Message>
      <Message>
        {productKeys.map((productName, productIndex) => {
          const hasNextProduct = productKeys.length - 1 > productIndex;
          const sites = groupedProducts[productName];
          if (sites.length === 1) {
            return (
              <React.Fragment key={`product-${productName}-${productIndex}`}>
                <a
                  href={ADVANCED_SEARCH_URL[productName](sites[0].url, searchTerm)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    analytics.ui('advancedSearchLinks', 'click', {
                      hasResults,
                      product: productName,
                    })
                  }
                >
                  {productName}
                </a>
                {hasNextProduct && ', '}
              </React.Fragment>
            );
          }

          const visibleSites = sites.length > 3 ? sites.splice(0, 3) : sites;

          const siteNames = visibleSites.map((site: Product, siteIndex: number) => {
            const hasNextSite = visibleSites.length - 1 > siteIndex;
            return (
              <React.Fragment key={`product-${productName}-${productIndex}-${site.siteName}-${siteIndex}`}>
                <a
                  href={ADVANCED_SEARCH_URL[productName](site.url, searchTerm)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    analytics.ui('advancedSearchLinks', 'click', {
                      hasResults,
                      product: productName,
                    })
                  }
                >
                  {site.siteName}
                </a>
                {hasNextSite && ', '}
              </React.Fragment>
            );
          });

          return (
            <React.Fragment key={`product-${productName}-${productIndex}`}>
              {productName} ({siteNames}){hasNextProduct && ', '}
            </React.Fragment>
          );
        })}
      </Message>
    </React.Fragment>
  );
};

export default inject(({ productStore }) => ({ productStore }))(injectIntl(withAnalytics(ProductAdvancedSearchLinks)));

const messages = defineMessages({
  clickOnAdvancedSearchLinks: {
    defaultMessage: 'Try again with a different term, or search all results in one of your sites:',
    description: 'message shown when searching for a term on your work have no results',
    id: 'uchi.clickOnAdvancedSearchLinks',
  },
});
